"use client";

import { ArrowRight, Zap, Heart, Mic, Building, Lightbulb, Cpu, Brain } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SignatureKeynoteTopics() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "The Resilience Mindset",
    description: "Master the psychological tools required to navigate high-pressure environments and turn setbacks into strategic advantages.",
    Icon: Brain
  }, {
    number: "02",
    title: "Leading with Empathy",
    description: "Transform organizational culture by integrating emotional intelligence into every decision-making process and team interaction.",
    Icon: Heart
  }, {
    number: "03",
    title: "Command the Stage",
    description: "Public speaking and presence training for executives who need to inspire action and project absolute authority.",
    Icon: Mic
  }, {
    number: "04",
    title: "Strategic Momentum",
    description: "Learn the frameworks for maintaining high-performance output without sacrificing long-term mental clarity or team health.",
    Icon: Zap
  }, {
    number: "05",
    title: "The Trust Architecture",
    description: "Building unbreakable bonds within leadership tiers to ensure seamless execution of complex global strategies.",
    Icon: Building
  }, {
    number: "06",
    title: "Future-Proof Leadership",
    description: "Adapting your leadership style for the digital age, focusing on agility, rapid learning, and decentralized power.",
    Icon: Lightbulb
  }];
  return <section data-section-id="241" id="signature-keynote-topics" className="bg-background text-foreground py-20 md:py-28 lg:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            
            <h2 className="font-default font-black text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter text-foreground uppercase">
              High-impact insights for{" "}
              <span className="text-primary">modern leaders.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md lg:ml-auto font-default">
              Damien Cole delivers more than just speeches; he provides the psychological 
              blueprints for elite performance. Each topic is engineered to challenge 
              assumptions and ignite immediate organizational change.
            </p>
          </div>
        </div>

        
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/20">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border-l-4 border-transparent hover:border-primary transition-all duration-500 ease-out hover:-translate-y-0.5 shadow-md ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                
                <span className="absolute top-6 right-6 font-mono italic text-3xl text-muted-foreground/20 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                
                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.5} aria-hidden="true" />
                </div>

                
                <h3 className="font-default text-2xl font-bold text-foreground mb-3 leading-tight tracking-tight uppercase">
                  {service.title}
                </h3>

                
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm font-default">
                  {service.description}
                </p>

                
                <Link to="/AboutPage" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary hover:text-foreground transition-colors duration-300">
                  <span>View Details</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        
        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="link" className="text-foreground hover:text-primary font-mono uppercase tracking-widest text-sm h-auto p-0">
            <Link to="/AboutPage" className="inline-flex items-center gap-2 group">
              <span className="border-b-2 border-primary pb-1 transition-colors duration-300">
                Download Speaker Sheet
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}